// PendingSyncBanner — «есть несинхронизированное», когда сеть ЕСТЬ.
//
// Дыра, которую он закрывает: `usePendingSyncCount` читался ровно в одном месте
// — `OfflineBanner`, а тот возвращает `null` при online. То есть застрявшая
// очередь (чанки лекции, сообщения репетитора) при живой сети не давала ни
// одного сигнала: пользователь считал, что всё отправлено.
//
// Тон намеренно тихий (muted, не warning): очередь, которая рассасывается за
// секунду, — норма, а не авария. Поэтому баннер появляется только если счётчик
// держится ненулевым дольше `STUCK_AFTER_MS` — обычная отправка не мигает
// полосой, а реально застрявшая очередь перестаёт быть невидимой.
//
// Место — общий `<BannerStack>` под топбаром: он смонтирован в `__root`, его
// высота уже зарезервирована через `--banners-height`, и он одинаково работает
// на desktop web, mobile web и в Capacitor (в т.ч. на overlay-роутах).

import { IconCloudUp } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { usePendingSyncCount } from "@/hooks/use-pending-sync-count";

/** Сколько очередь должна простоять непустой, прежде чем это стоит показывать. */
const STUCK_AFTER_MS = 10_000;

export function PendingSyncBanner(): React.ReactElement | null {
	const isOnline = useOnlineStatus();
	const pending = usePendingSyncCount();
	const { t } = useTranslation("common");
	const [stuck, setStuck] = useState(false);

	const active = isOnline && pending > 0;
	useEffect(() => {
		if (!active) {
			setStuck(false);
			return;
		}
		const id = setTimeout(() => setStuck(true), STUCK_AFTER_MS);
		return () => clearTimeout(id);
	}, [active]);

	if (!active || !stuck) return null;

	return (
		<div
			role="status"
			aria-live="polite"
			// Фон непрозрачный: баннер живёт в `fixed`-стопке под шапкой, и
			// полупрозрачный `bg-muted/70` пропускал прокручиваемый контент насквозь.
			className="flex items-center justify-center gap-2 border-b border-border bg-[color-mix(in_oklab,var(--color-muted)_70%,var(--color-background))] px-4 py-2 text-sm text-muted-foreground"
		>
			<IconCloudUp size={16} stroke={1.75} className="shrink-0" aria-hidden />
			<span className="tabular-nums">{t("sync.pendingBanner", { count: pending })}</span>
		</div>
	);
}
